import { h, Component } from 'preact'
import CategorySelector, { ParentCategory, Group, isGroup } from '../../preact-components/category-selector/CategorySelector'
import { isChangeEvent } from '../../libs/typeGuards'

interface Props {
  onUpdate: (value: undefined | number) => void
  categories: Array<ParentCategory | Group>
  forum_id: number | null
  error?: string
  isKnowledgeBaseEnabled: boolean
  isGroupsEnabled: boolean
  hideLabel?: boolean
  newStyle?: boolean
}

interface State {
  publishIn: 'knowledgeBase' | 'community' | 'groups'
}

function hasCategoryWithId (categoryId: number) {
  return (parent: ParentCategory | Group) => {
    if (isGroup(parent)) {
      return parent.groupId === categoryId
    }
    return parent.categories?.map(c => c.categoryId).includes(categoryId)
  }
}

export default class CategorySelect extends Component<Props, State> {
  constructor (props: Props) {
    super()

    let publishIn: State['publishIn'] = 'community'

    if (props.forum_id) {
      const grouppedCategories = this.groupCategories(props)
      if (grouppedCategories.groups.some(hasCategoryWithId(props.forum_id))) {
        publishIn = 'groups'
      } else if (grouppedCategories.knowledgeBase.some(hasCategoryWithId(props.forum_id))) {
        publishIn = 'knowledgeBase'
      }
    }

    this.state = { publishIn }

    this.handleRadioInputChange = this.handleRadioInputChange.bind(this)
  }

  componentDidUpdate (prevProps: Readonly<Props>) {
    let publishIn: State['publishIn'] = 'community'
    if (this.props.forum_id && this.props.forum_id !== prevProps.forum_id) {
      const grouppedCategories = this.groupCategories(this.props)
      if (grouppedCategories.groups.some(hasCategoryWithId(this.props.forum_id))) {
        publishIn = 'groups'
      } else if (grouppedCategories.knowledgeBase.some(hasCategoryWithId(this.props.forum_id))) {
        publishIn = 'knowledgeBase'
      }
      this.setState({ publishIn: publishIn })
    }
  }

  handleRadioInputChange (event: Event) {
    if (!isChangeEvent(event)) return
    const value = event.target.value
    if (value === 'groups' || value === 'knowledgeBase' || value === 'community') {
      this.setState({ publishIn: value })
    }
    this.props.onUpdate(undefined)
  }

  groupCategories (props: Props) {
    const {
      categories,
      isKnowledgeBaseEnabled,
      isGroupsEnabled
    } = props

    let filteredKnowledgeBaseCategories: Props['categories'] = []
    let filteredGroupCategories: Props['categories'] = []

    if (isKnowledgeBaseEnabled) {
      filteredKnowledgeBaseCategories = categories.filter(categoryGroup => categoryGroup.type === 1)
    }

    if (isGroupsEnabled) {
      filteredGroupCategories = categories.filter(isGroup)
    }

    const filteredCommunityCategories = categories.filter(categoryGroup => categoryGroup.type !== 1 && !isGroup(categoryGroup))

    return {
      knowledgeBase: filteredKnowledgeBaseCategories,
      community: filteredCommunityCategories,
      groups: filteredGroupCategories
    }
  }

  render (props: Props, state: State) {
    const {
      error,
      onUpdate,
      hideLabel,
      newStyle
    } = props
    const { publishIn } = state
    let className = 'category-select form-group'
    if (error) {
      className += ' error'
    }

    const grouppedCategories = this.groupCategories(this.props)

    const filteredCategories: Props['categories'] = grouppedCategories[state.publishIn]

    return (
      <div class={className}>
        {!hideLabel && <h2 className='category-select__heading'>Publish in *</h2>}

        <div class='category-select__publish-in'>
          {grouppedCategories.community.length
            ? <div class='category-select__radio-box'>
              <input
                id='communityCategory'
                class='category-select__radio-box__input'
                type='radio'
                name='publishIn'
                onChange={this.handleRadioInputChange}
                value='community'
                checked={publishIn === 'community'}
              />
              <label class='category-select__radio-box__label' for='communityCategory'>Community</label>
            </div>
            : null}

          {grouppedCategories.knowledgeBase.length
            ? <div class='category-select__radio-box category-select__radio-box__knowledgeBase'>
              <input
                id='knowledgeBaseCategory'
                class='category-select__radio-box__input'
                type='radio'
                name='publishIn'
                onChange={this.handleRadioInputChange}
                value='knowledgeBase'
                checked={publishIn === 'knowledgeBase'}
              />
              <label class='category-select__radio-box__label' for='knowledgeBaseCategory'>Knowledge Base</label>
            </div>
            : null}

          {grouppedCategories.groups.length
            ? <div class='category-select__radio-box category-select__radio-box__groups'>
              <input
                id='groupsCategory'
                class='category-select__radio-box__input'
                type='radio'
                name='publishIn'
                onChange={this.handleRadioInputChange}
                value='groups'
                checked={publishIn === 'groups'}
              />
              <label class='category-select__radio-box__label' for='groupsCategory'>Groups</label>
            </div>
            : null}
        </div>

        <CategorySelector
          categories={filteredCategories}
          selectedCategoryId={props.forum_id || undefined}
          onUpdate={(value: number) => onUpdate(value)}
          newStyle={newStyle}
        />
      </div>
    )
  }
}
